import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  OrganisationContext,
  ReportsDownloadUpdate,
  UserContext,
} from "../../../layouts/MainLayout";

/**Third Part Libraries */
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, TimePicker } from "antd";

/**Custom Components**/
import DateCalendar from "../../../utils/components/common/date_time_picker/DateCalendar";
import LoadingSpinner from "../../../utils/components/common/loadingSpinner/LoadingSpinner";
import ModalComponent from "../../../utils/components/common/modalComponent/ModalComponent";
import SearchBox from "../../../utils/components/common/searchBox/deviceSearchBox";
import Icons from "../../../utils/components/icons";

/**Services and generic functions**/
import {
  generateSummaryCSVReport,
  getSummaryDetailsData,
} from "../../../services/ReportService";
import {
  formatAMPM,
  roundToTwoDec,
  timeStringToTimeAMPM,
} from "../../../utils/utils";

/**Style**/
import "../ReportDetails.scss";
import DropDown from "../../../utils/components/common/dropdown/dropdown";
import EmptyScreen from "../../../utils/components/common/EmptyScreen/EmptyScreen";
import Pagination from "../../../utils/components/common/pagination";

const SummaryDetailsReport = () => {
  const { t } = useTranslation("reports");

  const [isDateWise, setIsDateWise] = useState(false);

  /**Location related */
  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = location?.pathname?.split("/")[2] || "";

  /**Contexts */
  const { reportUpdateStatus } = useContext(ReportsDownloadUpdate);

  /**Calendar related states starts */
  const [durationErr, setDurationErr] = useState({
    isErr: false,
    errMsg: "",
  });
  const [selectedDuration, setSelectedDuration] = useState([
    new Date().toISOString(),
    new Date(new Date() - 1000 * 60 * 60).toISOString(),
  ]);
  /**Calendar related states ends */

  const searchBoxRef = useRef();
  const [selectedDevice, setSelectedDevice] = useState();
  const [refreshTime, setRefreshTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [isTimeSelected, setIsTimeSelected] = useState(false);

  const [savedDates, setSavedDates] = useState([]);
  const [savedTimes, setSavedTimes] = useState([]);

  const [summaryData, setSummaryData] = useState([]);

  const [summaryErr, setSummaryErr] = useState(false);
  const [executionId, setExecutionId] = useState();
  const [reportJobId, setReportJobId] = useState();
  const [isDataValid, setIsDataValid] = useState(false);

  const [timerSelected, setTimerSelected] = useState("intervalType");
  const [isCustomTimerSelected, setIsCustomTimerSelected] = useState(false);

  const [pageTotalCount, setPageTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const moment = require("moment");
  const dateFormat = 'YYYY/MM/DD';

  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  /**Context* section starts*/
  const { userCognitoData } = useContext(UserContext);
  const { siteData, orgData } = useContext(OrganisationContext);
  const { summaryReportStatus, setSummaryReportStatus } = useContext(
    ReportsDownloadUpdate
  );
  /**Context* section ends*/

  /**Go back to device view**/
  const goBack = useCallback(() => {
    navigate("/reports");
  }, [navigate]);

  /**Update the filterState for device */
  const updateSelectedDevice = useCallback(() => {
    const deviceList = searchBoxRef.current.getSelectedList();
    const deviceIdList = deviceList.map((list) => {
      const id = list.PK.slice(2);
      return id;
    });
    setCurrentPage(1);
    setSelectedDevice(deviceIdList.length ? deviceIdList : undefined); //Add above deviceIdList once search filter code is done
  }, []);

  const intervalTypeArr = useMemo(
    () => [
      {
        label: t("reportFilter.summaryDetails.intervalType"),
        value: "intervalType",
      },
      {
        label: t("reportFilter.summaryDetails.timeIntervalBased"),
        value: "timeIntervalBased",
      },
      {
        label: t("reportFilter.summaryDetails.dateWiseTimeIntervalBased"),
        value: "dateWiseTimeIntervalBased",
      }
    ],
    [t]
  );

  const fetchArray = useCallback((type) => {
    if(type === "timeIntervalBased"){
      return "Time Interval Based";
    } else if(type === "dateWiseTimeIntervalBased"){
      return "Date-Wise and Time Interval Based";
    }
  },[])

  /**Callback : Generate CSV */
  const onCsvClick = useCallback(() => {
    const queryParam = {
      userId: userCognitoData?.username,
      tenantId: orgData?.TenantId,
      siteId: siteData?.id,
      deviceId: selectedDevice[0],
      reportType: "Device Summary",
      intervalType: fetchArray(timerSelected),
      isDateWiseReport: isDateWise,
      userTimezone: moment.tz.guess(),
      filters: {
        timeStamp: {
          between: selectedDuration,
        },
      },
      sort: [
        {
          timeStamp: "desc",
        },
      ],
    };

    setIsLoading(true);
    generateSummaryCSVReport(queryParam)
      .then((response) => {
        setIsLoading(false);
        setShowDownloadModal(true);
      })
      .catch((e) => {
        setIsLoading(false);
      });
  }, [
    userCognitoData?.username,
    orgData?.TenantId,
    siteData?.id,
    selectedDevice,
    timerSelected,
    isDateWise,
    moment.tz,
    selectedDuration,
    intervalTypeArr,
  ]);

  /**Update the download icon on new report download mqtt event**/
  const newDownloadNotification = useMemo(() => {
    const reportTypeString = {
      "device-parameters": "Device Parameter List",
      "device-diagnostics": "Device Diagnostics",
    };
    const data = reportUpdateStatus.find(
      (val) => val.reportType === reportTypeString[currentRoute]
    );
    return !!data;
  }, [currentRoute, reportUpdateStatus]);

  const updateTimeStamp = useCallback((time) => {
    const newTimeStamp = time;
    const timestamp1 = new Date(newTimeStamp[0]);
    const timestamp2 = new Date(newTimeStamp[1]);

    if (timestamp1 < timestamp2) {
      setSelectedDuration((prevVal) => {
        if (newTimeStamp.length) {
          setIsDateWise(false);
          return [...newTimeStamp];
        }
        return prevVal;
      });
    }
  }, []);

  const convertToUTC = (dateTimeArray) => {
    const utcDateTimeArray = dateTimeArray.map(([dateStr, timeStr]) => {
      const combinedDateTime = moment(
        `${dateStr} ${timeStr}`,
        "YYYY-MM-DD h:mm A"
      ).toISOString();
      return combinedDateTime;
    });

    return utcDateTimeArray;
  };

  useEffect(() => {
    if (timerSelected === "dateWiseTimeIntervalBased") {
      setIsTimeSelected(false);
      setSummaryData([]);
      setSavedDates([]);
      setSavedTimes([]);
    }
  }, [timerSelected]);

  const updateTimeStampCustomCalendar = (
    dateString,
    timeString,
    onlyDate = false,
    onlyTime = false
  ) => {
    if (onlyDate && dateString) {
      setSavedDates(dateString);
      setSavedTimes([]);
    }

    if (onlyTime && timeString) {
      setSavedTimes(timeString);
    }

    // To combine the updated dates and times
    const combinedDateTime = [
      [
        onlyDate ? dateString[0] : savedDates[0],
        onlyTime ? timeString[0] : savedTimes[0],
      ],
      [
        onlyDate ? dateString[1] : savedDates[1],
        onlyTime ? timeString[1] : savedTimes[1],
      ],
    ];

    const validCombinedDateTime = combinedDateTime.every(
      (dateTime) => dateTime[0] !== undefined && dateTime[1] !== undefined
    );

    if (validCombinedDateTime) {
      const utcArray = convertToUTC(combinedDateTime);
      setSelectedDuration(utcArray);
      setIsDateWise(true);
      setIsTimeSelected(false);

      let retentionErr = retentionError;

      let result = rangeError;

      if (result || retentionErr) {
        setIsTimeSelected(false);
      }
    }
    setCurrentPage(1);
  };

  const savedTimeDateChecker = useCallback(() => {
    if (
      savedTimes[0] === "" ||
      savedDates[0] === "" ||
      savedTimes.length === 0
    ) {
      setIsTimeSelected(false);
    } else {
      setIsTimeSelected(true);
    }
  }, [savedDates, savedTimes]);

  useEffect(() => {
    savedTimeDateChecker();
  }, [savedTimes, savedDates, savedTimeDateChecker]);

  const rangeError = useMemo(() => {
    const diff = new Date(selectedDuration[1]) - new Date(selectedDuration[0]);
    const sixMonthsInMillis = 6 * 30 * 24 * 60 * 60 * 1000; // Assuming 30 days per month

    return diff > sixMonthsInMillis;
  }, [selectedDuration]);

  const retentionError = useMemo(() => {
    const diff = new Date(selectedDuration[1]) - new Date(selectedDuration[0]);
    const oneYearInMillis = 12 * 30 * 24 * 60 * 60 * 1000; // Assuming 30 days per month

    if (diff > oneYearInMillis) {
      setDurationErr({
        isErr: true,
        errMsg: t("durationError"),
      });

      return diff > oneYearInMillis;
    } else {
      setDurationErr({
        isErr: false,
        errMsg: "",
      });
    }
  }, [selectedDuration, t]);

  /** Sequence-2 :  Call to getSummaryDetailsData API to start report generation at cloud end*/
  const getDeviceSummaryData = useCallback(async (queryParams) => {
    try {
      setSummaryErr(false);
      setIsSummaryLoading(true);
      setSummaryData();
      const response = await getSummaryDetailsData(queryParams);
      setExecutionId(response?.data?.executionId);
      setReportJobId(response?.data?.jobId);
      setPageTotalCount(response?.data?.details?.pageCount);
      setRefreshTime(new Date());
    } catch (err) {
      setSummaryData();
      setSummaryErr(true);
    }
  }, []);

  /** Sequence-1: UseEffect to set query Param for getSummaryDetailsData **/
  useEffect(() => {
    setSummaryData();

    const isDataComplete =
      siteData?.id &&
      userCognitoData?.username &&
      orgData?.TenantId &&
      selectedDevice &&
      selectedDevice?.length &&
      !durationErr.isErr &&
      isTimeSelected &&
      !rangeError;

    if (!isDataComplete) {
      setIsDataValid(false);
      return;
    }

    if (selectedDevice.length > 1) {
      setSummaryData();
      return;
    }

    setIsDataValid(true);

    const queryParams = {
      userId: userCognitoData?.username,
      tenantId: orgData?.TenantId,
      siteId: siteData?.id,
      deviceId: selectedDevice[0],
      isDateWiseReport: isDateWise,
      pageNo: currentPage,
      filters: {
        timeStamp: {
          between: selectedDuration,
        },
      },
      sort: [
        {
          timeStamp: "asc",
        },
      ],
      userTimezone: moment.tz.guess()
    };
    setIsSummaryLoading(true);
    getDeviceSummaryData(queryParams);
  }, [
    getDeviceSummaryData,
    orgData?.TenantId,
    isDateWise,
    selectedDevice,
    siteData?.id,
    userCognitoData?.username,
    selectedDuration,
    durationErr,
    isTimeSelected,
    rangeError,
    currentPage,
    savedTimes,
    savedDates,
    moment.tz
  ]);

  /** Sequence-3: UseEffect to fetch report data on getting MQTT event [summaryReportStatus]
   **/
  useEffect(() => {
    if (
      summaryReportStatus &&
      reportJobId === summaryReportStatus?.reportJobId
    ) {
      const getDataFronS3Bucket = async () => {
        try {
          const fetchResponse = await fetch(summaryReportStatus?.signedUrl);
          setSummaryReportStatus(null);
          const response = await fetchResponse.json();
          setExecutionId();
          setSummaryData(response);
          setIsSummaryLoading(false);
        } catch (err) {
          setIsSummaryLoading(false);
          console.log(err);
          setSummaryData();
          setSummaryErr(true);
        }
      };
      if (
        summaryReportStatus?.reportStatus === "Completed" &&
        summaryReportStatus?.signedUrl
      ) {
        getDataFronS3Bucket();
      }

      if (summaryReportStatus?.reportStatus === "Failed") {
        setIsSummaryLoading(false);
        setSummaryErr(true);
      }
    }
  }, [summaryReportStatus, setSummaryReportStatus, reportJobId, executionId]);

  /**Update err Msg **/
  const emptyMsg = useMemo(() => {
    if (durationErr.isErr) {
      return {
        title: t("no_data_to_display"),
        desc: durationErr.errMsg,
      };
    }
    if (rangeError) {
      return {
        title: t("no_data_to_display"),
        desc: t("rangeError", {
          maxRange: "6 months",
        }),
      };
    }

    if (
      !selectedDevice?.length ||
      timerSelected === "Interval Type" ||
      !isDataValid
    ) {
      return {
        title: t("no_data_to_display"),
        desc: t("noDataMsg.deviceIntervalType"),
      };
    }
    if (summaryErr) {
      return {
        title: t("no_data_to_display"),
        desc: t("downloadsFailed"),
      };
    }
    return {
      title: "",
      desc: "",
    };
  }, [
    durationErr,
    selectedDevice,
    summaryErr,
    t,
    rangeError,
    isDataValid,
    timerSelected,
  ]);

  // Function to disable future dates
  const disabledFutureDates = (current) => {
    return current && current > new Date();
  };

  const formatApiValue = useCallback((value, type) => {
    if (!value && value !== 0) return "-";
    return roundToTwoDec(value);
  }, []);

  const handlePageChange = useCallback((item) => {
    setCurrentPage(item + 1);
  }, []);

  return (
    <div className="bodyInner summaryReportPage reportPageMain reportPageSummry">
      {siteData?.id ? (
        <div className="bodyContainer">
          {/* Header Starts  */}
          <div className="controllesWrapper header fixSubHeader">
            <div className="left-header page-heading">
              <span className="heading-1" onClick={goBack}>
                {t("reportsHeading")}
              </span>
              <span className="heading-2">
                /{t("deviceSummaryReportHeading")}
              </span>
            </div>
            <div className="right-header">
              <div>
                <SearchBox
                  ref={searchBoxRef}
                  autoid="report-search-device"
                  placeholderText={t("deviceSearch.placeholder")}
                  titleText={t("deviceSearch.title")}
                  getOptionCallBack={() => {
                    updateSelectedDevice();
                  }}
                  isMultiSelect={false}
                />
              </div>
              <span className="enabledIcon header-icons">
                <button
                  className={`verticalBtn
                  ${newDownloadNotification ? "" : "hide-circle"}`}
                  onClick={() => navigate("/reports/downloads")}
                  title={t("downloads")}
                >
                  {Icons.downalodStatusIcon}
                </button>
              </span>
              <span className="disabledIcon header-icons">
                <button
                  className={"verticalBtn"}
                  disabled
                  title={t("mark_favorite")}
                >
                  {Icons.favoriteIcon}
                </button>
              </span>
              <span className="disabledIcon header-icons">
                <button
                  className="verticalBtn"
                  disabled
                  title={t("export_as_pdf")}
                >
                  {Icons.pdfIcon}
                </button>
              </span>
              <span className="header-icons">
                <button
                  className="verticalBtn"
                  onClick={onCsvClick}
                  disabled={!summaryData?.length}
                  title={t("export_as_csv")}
                >
                  {Icons.csvIcon}
                </button>
              </span>
              <span className="disabledIcon header-icons">
                <button className="verticalBtn" disabled title={t("print")}>
                  {Icons.printIcon}
                </button>
              </span>
            </div>
          </div>
          <div className="body-container-wrapper">
            {/* Header Ends  */}

            {/* Body Starts */}

            <div className="summaryBody">
              <div className="upperSection">
                <div className="left-section"></div>

                <div className="rightSection">
                  <DropDown
                    options={intervalTypeArr}
                    wrapperClass={`reportSummary ${
                      isCustomTimerSelected ? `selected` : ``
                    }`}
                    autoid="reportSummary"
                    value={timerSelected}
                    onSelection={(val) => {
                      setTimerSelected(val);
                      setIsCustomTimerSelected(true);
                    }}
                  />

                  {timerSelected === "dateWiseTimeIntervalBased" ? (
                    <div className="calendarSection">
                      <span
                        className={
                          savedDates.length === 2 && savedDates[0] !== ""
                            ? "active"
                            : ""
                        }
                      >
                        <DatePicker.RangePicker
                          disabledDate={disabledFutureDates}
                          size="large"
                          onChange={(date, dateString) => {
                            updateTimeStampCustomCalendar(
                              dateString,
                              [undefined, undefined],
                              true,
                              false
                            );
                          }}
                          format={dateFormat}
                          onKeyDown={(e) => { e.preventDefault(); }}
                          placeholder={['Start Date', 'End Date']}
                          suffixIcon={Icons.calendarIcon}
                        />
                      </span>

                      {savedDates.length === 2 && savedDates[0] !== "" ? (
                        <span className={isTimeSelected ? "active" : ""}>
                          <TimePicker.RangePicker
                            use12Hours
                            format="hh:mm A"
                            size="large"
                            onChange={(time, timeString) => {
                              updateTimeStampCustomCalendar(
                                [undefined, undefined],
                                timeString,
                                false,
                                true
                              );
                            }}
                            placeholder={['Start Time', 'End Time']}
                            suffixIcon={Icons.clockPurpleIcon}
                            order={false}
                          />
                        </span>
                      ) : (
                        <>
                          <TimePicker.RangePicker
                            size="large"
                            disabled={true}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <DateCalendar
                        getSelectedDate={(
                          timeStampRange,
                          isCustomChecker,
                          isErrorActive,
                          errorMessage,
                          isTimeStampSelected
                        ) => {
                          setDurationErr({
                            isErr: isErrorActive,
                            errMsg: errorMessage,
                          });
                          updateTimeStamp(timeStampRange);
                          setIsTimeSelected(isTimeStampSelected);
                        }}
                        noDefaultSelected={true}
                        disableOption={timerSelected === "intervalType"}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="data-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th id="timeDurationHeader">{t(`tableHeading.summaryDetails.timeDuration`)}</th>
                      <th>{t(`tableHeading.summaryDetails.targetAtStart`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgPPB`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minPPB`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxPPB`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgTempInC`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgTempInF`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgHumidity`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgBaro`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgShotSize`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minShotSize`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxShotSize`)}</th>
                      <th>{t(`tableHeading.summaryDetails.sumShotSize`)}</th>
                      <th>{t(`tableHeading.summaryDetails.totalShots`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp1Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp1Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp1Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp1Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp1Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp1Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp2Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp2Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp2Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp2Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp2Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp2Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp3Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp3Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp3Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp3Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp3Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp3Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp4Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp4Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp4Voc`)}</th>
                      <th>{t(`tableHeading.summaryDetails.avgSgp4Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.minSgp4Nox`)}</th>
                      <th>{t(`tableHeading.summaryDetails.maxSgp4Nox`)}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {summaryData?.length ? (
                      <>
                        {summaryData?.map((data, i) => {
                          return (
                            <tr key={`${i + 1}`}>
                              <td id="timeDurationValue">
                                {timeStringToTimeAMPM(
                                  data?.timeStamp?.startTime
                                )}
                                -
                                {timeStringToTimeAMPM(data?.timeStamp?.endTime)}
                              </td>
                              <td>{formatApiValue(data?.targetAtStart)}</td>
                              <td>{formatApiValue(data?.avgPPB)}</td>
                              <td>{formatApiValue(data?.minPPB)}</td>
                              <td>{formatApiValue(data?.maxPPB)}</td>
                              <td>{formatApiValue(data?.avgTemperatureInC)}</td>
                              <td>{formatApiValue(data?.avgTemperatureInF)}</td>
                              <td>{formatApiValue(data?.avgHumidity)}</td>
                              <td>
                                {formatApiValue(data?.avgBarometricPressure)}
                              </td>
                              <td>{formatApiValue(data?.avgShotSize)}</td>
                              <td>{formatApiValue(data?.minShotSize)}</td>
                              <td>{formatApiValue(data?.maxShotSize)}</td>
                              <td>{formatApiValue(data?.sumShotSize)}</td>
                              <td>{formatApiValue(data?.TotalShotSize)}</td>

                              <td>{formatApiValue(data?.avgsgp4x1VoC)}</td>
                              <td>{formatApiValue(data?.minsgp4x1VoC)}</td>
                              <td>{formatApiValue(data?.maxsgp4x1VoC)}</td>

                              <td>{formatApiValue(data?.avgsgp4x1NOx)}</td>
                              <td>{formatApiValue(data?.minsgp4x1NOx)}</td>
                              <td>{formatApiValue(data?.maxsgp4x1NOx)}</td>

                              <td>{formatApiValue(data?.avgsgp4x2VoC)}</td>
                              <td>{formatApiValue(data?.minsgp4x2VoC)}</td>
                              <td>{formatApiValue(data?.maxsgp4x2VoC)}</td>

                              <td>{formatApiValue(data?.avgsgp4x2NOx)}</td>
                              <td>{formatApiValue(data?.minsgp4x2NOx)}</td>
                              <td>{formatApiValue(data?.maxsgp4x2NOx)}</td>

                              <td>{formatApiValue(data?.avgsgp4x3VoC)}</td>
                              <td>{formatApiValue(data?.minsgp4x3VoC)}</td>
                              <td>{formatApiValue(data?.maxsgp4x3VoC)}</td>

                              <td>{formatApiValue(data?.avgsgp4x3NOx)}</td>
                              <td>{formatApiValue(data?.minsgp4x3NOx)}</td>
                              <td>{formatApiValue(data?.maxsgp4x3NOx)}</td>

                              <td>{formatApiValue(data?.avgsgp4x4VoC)}</td>
                              <td>{formatApiValue(data?.minsgp4x4VoC)}</td>
                              <td>{formatApiValue(data?.maxsgp4x4VoC)}</td>

                              <td>{formatApiValue(data?.avgsgp4x4NOx)}</td>
                              <td>{formatApiValue(data?.minsgp4x4NOx)}</td>
                              <td>{formatApiValue(data?.maxsgp4x4NOx)}</td>
                            </tr>
                          );
                        })}
                      </>
                    ) : (
                      <tr>
                        <td colSpan="40" className="empty-screen-td">
                          <div className="empty-screen-wrapper">
                            {emptyMsg.title !== "" && (
                              <EmptyScreen
                                img={Icons.emptyScreenReports}
                                small={true}
                                {...emptyMsg}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Body Ends */}

            {/* Footer Starts  */}

            {summaryData?.length >= 0 && selectedDevice?.length ? (
              <div className="bodyFooter footerFixed">
                {/* Pagination */}

                <Pagination
                  currentPage={currentPage - 1}
                  totalPages={pageTotalCount}
                  pageCallBack={handlePageChange}
                />

                <div className="footer-time">
                  <span className="text-h4">
                    {t("reportGeneratedOn", {
                      time: formatAMPM(refreshTime, false),
                    })}
                  </span>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          {/* Footer Ends  */}
          {isSummaryLoading && <LoadingSpinner />}
          {isLoading && <LoadingSpinner />}
        </div>
      ) : (
        <></>
      )}
      <ModalComponent
        idName="csv-download-modal"
        isOpen={showDownloadModal}
        onCloseModal={() => setShowDownloadModal(false)}
        onConfirm={() => navigate("/reports/downloads")}
        descriptionText={t("downloadModalMsg")}
        primaryBtnText={t("goToDownload")}
        primaryTextIcon={Icons.maximizeIcon}
        secondaryBtnText={t("close")}
        needConfirmation={true}
      ></ModalComponent>
    </div>
  );
};

export default SummaryDetailsReport;
